import type { ReactNode } from "react";
import {
  Switch as AriaSwitch,
  type SwitchProps as AriaSwitchProps,
  composeRenderProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Information } from "./information";
import { focusRing } from "./utils";

const switchStyles = tv({
  base: "flex items-center gap-2 group text-sm transition cursor-pointer",
  variants: {
    isDisabled: {
      false: "text-gray-800",
      true: "text-gray-300 cursor-default forced-colors:text-[GrayText]",
    },
  },
});

const trackStyles = tv({
  extend: focusRing,
  base: "relative rounded-full border-2 border-transparent transition-colors shrink-0",
  variants: {
    isSelected: {
      false:
        "bg-gray-300 group-pressed:bg-gray-400 forced-colors:bg-[ButtonBorder]",
      true: "bg-gray-700 group-pressed:bg-gray-800 forced-colors:bg-[Highlight]!",
    },
    isDisabled: {
      true: "bg-gray-200 forced-colors:bg-[GrayText]!",
    },
    hasLabels: {
      false: "w-9 h-5",
      true: "w-[4.5rem] h-6",
    },
  },
  defaultVariants: {
    hasLabels: false,
  },
});

const thumbStyles = tv({
  base: "block rounded-full bg-white shadow transition-transform will-change-transform translate-x-0",
  variants: {
    hasLabels: {
      false: "w-4 h-4 group-selected:translate-x-4",
      true: "w-5 h-5 group-selected:translate-x-12",
    },
  },
  defaultVariants: {
    hasLabels: false,
  },
});

const trackLabelStyles = tv({
  base: "text-[10px] font-semibold uppercase select-none leading-none",
  variants: {
    isSelected: {
      false: "text-gray-500",
      true: "text-white",
    },
  },
});

export type SwitchProps = AriaSwitchProps & {
  label: ReactNode;
  info?: ReactNode;
  /** Text shown inside the track when the switch is off */
  offLabel?: string;
  /** Text shown inside the track when the switch is on */
  onLabel?: string;
};

export function Switch({
  label,
  info,
  offLabel,
  onLabel,
  ...props
}: SwitchProps) {
  const hasLabels = !!(offLabel || onLabel);

  return (
    <AriaSwitch
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        switchStyles({ ...renderProps, className }),
      )}
    >
      {({ isSelected, ...renderProps }) => (
        <>
          <div
            className={trackStyles({ isSelected, hasLabels, ...renderProps })}
          >
            {hasLabels && (
              <div className="absolute inset-0 flex items-center justify-between px-1.5">
                <span
                  className={trackLabelStyles({
                    isSelected: true,
                  })}
                >
                  {isSelected ? onLabel : ""}
                </span>
                <span
                  className={trackLabelStyles({
                    isSelected: false,
                  })}
                >
                  {!isSelected ? offLabel : ""}
                </span>
              </div>
            )}
            <span className={thumbStyles({ hasLabels })} />
          </div>
          <span className="flex flex-row items-center space-x-2">
            <span>{label}</span>
            {info && <Information>{info}</Information>}
          </span>
        </>
      )}
    </AriaSwitch>
  );
}
